import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LayoutGrid, LogOut, Menu, ScrollText, Shield, SlidersHorizontal, User, Users, X } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { BrandMark } from './BrandMark';
import { pageVariants } from '../portal/portalConfig';
import { prefetchJsonCached } from '../utils/portalQueryCache';

function resolveAndRedirect(target) {
  if (!target) {
    return false;
  }

  if (target.startsWith('http://') || target.startsWith('https://')) {
    window.location.assign(target);
    return true;
  }

  return false;
}

export function ShellLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  React.useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    prefetchJsonCached('/api/profile', { ttlMs: 120_000, signal: controller.signal });
    prefetchJsonCached('/api/tools', { ttlMs: 60_000, signal: controller.signal });

    if (['HK-Admin', 'Group-Admin', 'Store-Admin'].includes(user.role)) {
      prefetchJsonCached('/api/admin/roles/options', { ttlMs: 300_000, signal: controller.signal });
      prefetchJsonCached('/api/admin/roles/users', { ttlMs: 30_000, signal: controller.signal });
      prefetchJsonCached('/api/admin/roles/groups', { ttlMs: 300_000, signal: controller.signal });
      prefetchJsonCached('/api/admin/roles/stores', { ttlMs: 300_000, signal: controller.signal });
    }

    if (user.role === 'HK-Admin') {
      prefetchJsonCached('/api/admin/logs?page=1&pageSize=20', { ttlMs: 30_000, signal: controller.signal });
    }

    return () => controller.abort();
  }, [user]);

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const onLogout = React.useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      // Always land on the login screen after logout (avoids "bounce" through protected pages).
      const redirectUrl = await logout('/login');
      if (!redirectUrl) {
        return;
      }

      if (resolveAndRedirect(redirectUrl)) {
        return;
      }

      navigate(redirectUrl || '/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, logout, navigate]);

  const navItems = React.useMemo(() => {
    const role = user?.role;
    const isPortalAdmin = ['HK-Admin', 'Group-Admin', 'Store-Admin'].includes(role);

    return [
      { to: '/tools', label: 'Tools', icon: LayoutGrid },
      { to: '/profile', label: 'Profile', icon: User },
      ...(isPortalAdmin ? [{ to: '/users', label: 'Users', icon: Users }] : []),
      ...(isPortalAdmin ? [{ to: '/platform-access', label: 'Platform Access', icon: SlidersHorizontal }] : []),
      ...(role === 'HK-Admin' ? [{ to: '/logs', label: 'Logs', icon: ScrollText }] : []),
      ...(role === 'HK-Admin' ? [{ to: '/admin', label: 'Admin', icon: Shield }] : []),
    ];
  }, [user?.role]);

  return (
    <div className="portal-shell">
      <aside className="portal-sidebar">
        <div>
          <BrandMark variant="sidebar" />

          <nav className="portal-nav">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}>
                <item.icon size={16} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <motion.button type="button" className="ghost-button logout-button" onClick={onLogout} whileTap={{ scale: 0.97 }} disabled={isLoggingOut}>
          <LogOut size={16} />
          <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
        </motion.button>
      </aside>

      <main className="portal-content">
        <div className="mobile-topbar">
          <div className="mobile-topbar-left">
            <img className="mobile-topbar-logo" src="/hk-login-logo.png" alt="HK Identity" />
            <span className="mobile-topbar-title">HK Identity</span>
          </div>
          <button
            type="button"
            className="mobile-burger-button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation menu"
          >
            <Menu size={18} />
          </button>
        </div>

        <header className="content-header">
          <div className="content-header-user">
            {user?.avatarUrl ? (
              <img className="content-header-avatar-image" src={user.avatarUrl} alt="Profile" />
            ) : (
              <div className="content-header-avatar-fallback">
                {(user?.displayName || user?.email || 'HK').slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
            <h1>Welcome, {user?.displayName ?? 'User'}</h1>
            <p>{user?.email}</p>
            </div>
          </div>
          <motion.span
            key={user?.role ?? 'User'}
            className="role-pill"
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {user?.role ?? 'User'}
          </motion.span>
        </header>

        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {mobileNavOpen && (
          <motion.div
            className="mobile-nav-overlay"
            onClick={() => setMobileNavOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.aside
              className="mobile-nav-drawer"
              onClick={(event) => event.stopPropagation()}
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mobile-nav-header">
                <BrandMark variant="sidebar" />
                <button
                  type="button"
                  className="mobile-burger-button"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close navigation menu"
                >
                  <X size={18} />
                </button>
              </div>

              <nav className="portal-nav mobile-nav-list">
                {navItems.map((item) => (
                  <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}>
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>

              <button type="button" className="ghost-button logout-button mobile-logout-button" onClick={onLogout} disabled={isLoggingOut}>
                <LogOut size={16} />
                <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
              </button>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
